// src/components/admin/sections/AdminHeader.tsx
import React, { useState, useEffect } from 'react';
import apiElCristo, { HeaderData, MenuItem } from '../../../service/api_elcristo';

const AdminHeader: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const [headerData, setHeaderData] = useState<HeaderData>({
    logo: '',
    contactInfo: {
      phone: '',
      email: '',
      address: ''
    },
    socialLinks: {
      facebook: '',
      twitter: '',
      youtube: '',
      linkedin: ''
    },
    menuItems: [],
    ctaButton: {
      text: '',
      url: '',
      image: ''
    },
    chatConfig: {
      enabled: false,
      botName: '',
      welcomeMessage: '',
      position: 'bottom-right',
      primaryColor: '#ff0f00',
      buttonSize: 'medium',
      theme: 'light'
    }
  });

  const [showMenuModal, setShowMenuModal] = useState(false);
  const [editingMenuItem, setEditingMenuItem] = useState<MenuItem | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const [menuFormData, setMenuFormData] = useState<MenuItem>({
    text: '',
    url: '',
    subMenu: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await apiElCristo.getHeaderData();
      setHeaderData(data);
    } catch (error) {
      console.error('Error loading header:', error);
      setMessage({ type: 'error', text: 'Error al cargar el header' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, section: string, field?: string) => {
    const { value } = e.target;
    
    if (field) {
      setHeaderData(prev => ({
        ...prev,
        [section]: {
          ...prev[section as keyof HeaderData] as any,
          [field]: value
        }
      }));
    } else {
      setHeaderData(prev => ({
        ...prev,
        [section]: value
      }));
    }
  };

  const handleOpenMenuModal = (item?: MenuItem, index?: number) => {
    if (item && index !== undefined) {
      setEditingMenuItem(item);
      setEditingIndex(index);
      setMenuFormData(item);
    } else {
      setEditingMenuItem(null);
      setEditingIndex(null);
      setMenuFormData({
        text: '',
        url: '',
        subMenu: []
      });
    }
    setShowMenuModal(true);
  };

  const handleCloseMenuModal = () => {
    setShowMenuModal(false);
    setEditingMenuItem(null);
    setEditingIndex(null);
  };

  const handleMenuChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setMenuFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveMenuItem = () => {
    if (editingIndex !== null) {
      const updated = [...headerData.menuItems];
      updated[editingIndex] = menuFormData;
      setHeaderData(prev => ({ ...prev, menuItems: updated }));
    } else {
      setHeaderData(prev => ({
        ...prev,
        menuItems: [...prev.menuItems, menuFormData]
      }));
    }
    handleCloseMenuModal();
  };

  const handleDeleteMenuItem = (index: number) => {
    if (!window.confirm('¿Estás seguro de eliminar este ítem del menú?')) {
      return;
    }

    setHeaderData(prev => ({
      ...prev,
      menuItems: prev.menuItems.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      await apiElCristo.updateHeaderData(headerData);
      setMessage({ type: 'success', text: '✅ Header actualizado correctamente' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Error saving header:', error);
      setMessage({ type: 'error', text: '❌ Error al guardar: ' + error.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="elcristo-admin-content">
        <div className="elcristo-loading-spinner">
          <div className="elcristo-spinner"></div>
          <p>Cargando header...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="elcristo-admin-content">
      <div className="elcristo-content-header">
        <div>
          <h1><i className="fas fa-bars"></i> Editar Header & Menú</h1>
          <p>Configuración del encabezado y navegación del sitio</p>
        </div>
      </div>

      {message && (
        <div className={`elcristo-alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Logo Section */}
        <div className="elcristo-admin-form">
          <div className="elcristo-form-section">
            <h3><i className="fas fa-image"></i> Logo</h3>
            
            <div className="elcristo-form-group">
              <label htmlFor="logo">URL del Logo</label>
              <input
                type="text"
                id="logo"
                value={headerData.logo}
                onChange={(e) => handleChange(e, 'logo')}
                placeholder="/assets/kidsa/assets/img/logo/logoheader.png"
              />
              {headerData.logo && (
                <div className="elcristo-image-preview">
                  <img src={headerData.logo} alt="Logo" style={{ maxHeight: '60px' }} />
                </div>
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div className="elcristo-form-section">
            <h3><i className="fas fa-address-book"></i> Información de Contacto</h3>
            
            <div className="elcristo-form-row">
              <div className="elcristo-form-group">
                <label htmlFor="phone">Teléfono</label>
                <input
                  type="text"
                  id="phone"
                  value={headerData.contactInfo.phone}
                  onChange={(e) => handleChange(e, 'contactInfo', 'phone')}
                  placeholder="+56 9 4696 6778"
                />
              </div>

              <div className="elcristo-form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={headerData.contactInfo.email}
                  onChange={(e) => handleChange(e, 'contactInfo', 'email')}
                  placeholder="colegio@colegioelcristo.cl"
                />
              </div>
            </div>

            <div className="elcristo-form-group">
              <label htmlFor="address">Dirección</label>
              <input
                type="text"
                id="address"
                value={headerData.contactInfo.address}
                onChange={(e) => handleChange(e, 'contactInfo', 'address')}
                placeholder="Camino Interior Nº211, El Monte"
              />
            </div>
          </div>

          {/* Social Links */}
          <div className="elcristo-form-section">
            <h3><i className="fab fa-facebook"></i> Redes Sociales</h3>
            
            <div className="elcristo-form-row">
              <div className="elcristo-form-group">
                <label>
                  <i className="fab fa-facebook"></i> Facebook
                </label>
                <input
                  type="text"
                  value={headerData.socialLinks.facebook}
                  onChange={(e) => handleChange(e, 'socialLinks', 'facebook')}
                  placeholder="https://facebook.com/..."
                />
              </div>

              <div className="elcristo-form-group">
                <label>
                  <i className="fab fa-twitter"></i> Twitter
                </label>
                <input
                  type="text"
                  value={headerData.socialLinks.twitter}
                  onChange={(e) => handleChange(e, 'socialLinks', 'twitter')}
                  placeholder="https://twitter.com/..."
                />
              </div>
            </div>

            <div className="elcristo-form-row">
              <div className="elcristo-form-group">
                <label>
                  <i className="fab fa-youtube"></i> YouTube
                </label>
                <input
                  type="text"
                  value={headerData.socialLinks.youtube}
                  onChange={(e) => handleChange(e, 'socialLinks', 'youtube')}
                  placeholder="https://youtube.com/..."
                />
              </div>

              <div className="elcristo-form-group">
                <label>
                  <i className="fab fa-linkedin"></i> LinkedIn
                </label>
                <input
                  type="text"
                  value={headerData.socialLinks.linkedin}
                  onChange={(e) => handleChange(e, 'socialLinks', 'linkedin')}
                  placeholder="https://linkedin.com/..."
                />
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="elcristo-form-section">
            <h3><i className="fas fa-mouse-pointer"></i> Botón CTA (Call to Action)</h3>
            
            <div className="elcristo-form-row">
              <div className="elcristo-form-group">
                <label>Texto del Botón</label>
                <input
                  type="text"
                  value={headerData.ctaButton.text}
                  onChange={(e) => handleChange(e, 'ctaButton', 'text')}
                  placeholder="Admisión"
                />
              </div>

              <div className="elcristo-form-group">
                <label>URL del Botón</label>
                <input
                  type="text"
                  value={headerData.ctaButton.url}
                  onChange={(e) => handleChange(e, 'ctaButton', 'url')}
                  placeholder="/admision"
                />
              </div>
            </div>
          </div>

          {/* Chat Widget Config */}
          <div className="elcristo-form-section">
            <h3><i className="fas fa-comments"></i> Chat Widget</h3>
            
            <div className="elcristo-form-row">
              <div className="elcristo-form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={headerData.chatConfig?.enabled || false}
                    onChange={(e) => {
                      setHeaderData(prev => ({
                        ...prev,
                        chatConfig: {
                          ...prev.chatConfig,
                          enabled: e.target.checked
                        }
                      }));
                    }}
                    style={{ marginRight: '8px' }}
                  />
                  Habilitar Chat
                </label>
              </div>

              <div className="elcristo-form-group">
                <label>Nombre del Bot</label>
                <input
                  type="text"
                  value={headerData.chatConfig?.botName || ''}
                  onChange={(e) => handleChange(e, 'chatConfig', 'botName')}
                  placeholder="Asistente Virtual"
                />
              </div>
            </div>

            <div className="elcristo-form-group">
              <label>Mensaje de Bienvenida</label>
              <input
                type="text"
                value={headerData.chatConfig?.welcomeMessage || ''}
                onChange={(e) => handleChange(e, 'chatConfig', 'welcomeMessage')}
                placeholder="¡Hola! ¿En qué puedo ayudarte?"
              />
            </div>

            <div className="elcristo-form-row">
              <div className="elcristo-form-group">
                <label>Posición</label>
                <select
                  value={headerData.chatConfig?.position || 'bottom-right'}
                  onChange={(e) => handleChange(e, 'chatConfig', 'position')}
                >
                  <option value="bottom-right">Abajo Derecha</option>
                  <option value="bottom-left">Abajo Izquierda</option>
                </select>
              </div>

              <div className="elcristo-form-group">
                <label>Tamaño</label>
                <select
                  value={headerData.chatConfig?.buttonSize || 'medium'}
                  onChange={(e) => handleChange(e, 'chatConfig', 'buttonSize')}
                >
                  <option value="small">Pequeño</option>
                  <option value="medium">Mediano</option>
                  <option value="large">Grande</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <div className="menu-section">
          <div className="elcristo-section-header">
            <h3><i className="fas fa-bars"></i> Menú de Navegación ({headerData.menuItems.length})</h3>
            <button type="button" className="elcristo-btn-primary" onClick={() => handleOpenMenuModal()}>
              <i className="fas fa-plus"></i>
              Nuevo Ítem
            </button>
          </div>

          <div className="menu-list">
            {headerData.menuItems.map((item, index) => (
              <div key={index} className="menu-item-card">
                <div className="menu-item-content">
                  <h4>{item.text}</h4>
                  <p>{item.url}</p>
                  {item.subMenu && item.subMenu.length > 0 && (
                    <span className="submenu-badge">
                      {item.subMenu.length} submenú(s)
                    </span>
                  )}
                </div>
                <div className="menu-item-actions">
                  <button
                    type="button"
                    className="elcristo-btn-icon btn-edit"
                    onClick={() => handleOpenMenuModal(item, index)}
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button
                    type="button"
                    className="elcristo-btn-icon btn-delete"
                    onClick={() => handleDeleteMenuItem(index)}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            ))}

            {headerData.menuItems.length === 0 && (
              <div className="elcristo-empty-state">
                <i className="fas fa-bars"></i>
                <p>No hay ítems en el menú</p>
              </div>
            )}
          </div>
        </div>

        <div className="elcristo-form-actions">
          <button type="button" className="elcristo-btn-secondary" onClick={loadData} disabled={saving}>
            <i className="fas fa-undo"></i>
            Recargar
          </button>
          
          <button type="submit" className="elcristo-btn-primary" disabled={saving}>
            {saving ? (
              <>
                <span className="elcristo-spinner-small"></span>
                Guardando...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Guardar Cambios
              </>
            )}
          </button>
        </div>
      </form>

      {/* Menu Modal */}
      {showMenuModal && (
        <div className="elcristo-modal-overlay" onClick={handleCloseMenuModal}>
          <div className="elcristo-modal-content" onClick={e => e.stopPropagation()}>
            <div className="elcristo-modal-header">
              <h2>
                <i className="fas fa-bars"></i>
                {editingMenuItem ? 'Editar Ítem' : 'Nuevo Ítem'}
              </h2>
              <button className="elcristo-modal-close" onClick={handleCloseMenuModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="elcristo-modal-body">
              <div className="elcristo-form-group">
                <label>Texto del Menú *</label>
                <input
                  type="text"
                  name="text"
                  value={menuFormData.text}
                  onChange={handleMenuChange}
                  placeholder="Ej: Inicio, Nosotros, Contacto"
                  required
                />
              </div>

              <div className="elcristo-form-group">
                <label>URL *</label>
                <input
                  type="text"
                  name="url"
                  value={menuFormData.url}
                  onChange={handleMenuChange}
                  placeholder="Ej: ./ o ./#nosotros"
                  required
                />
              </div>

              <div className="elcristo-form-hint">
                Nota: Los submenús se gestionan directamente en el código por su complejidad.
                Este editor permite configurar los ítems principales del menú.
              </div>
            </div>

            <div className="elcristo-modal-footer">
              <button type="button" className="elcristo-btn-secondary" onClick={handleCloseMenuModal}>
                <i className="fas fa-times"></i>
                Cancelar
              </button>
              <button type="button" className="elcristo-btn-primary" onClick={handleSaveMenuItem}>
                <i className="fas fa-save"></i>
                {editingMenuItem ? 'Actualizar' : 'Agregar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHeader;